import { useCallback, useEffect, useRef } from 'react';

import qs from 'qs';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useCancelableNavigate } from '#src/app/shared/hooks/navigate';
import type { ShareChannel } from '#src/app/shared/hooks/share';
import { canRedirectAfterShare } from '#src/app/shared/share/webviewRedirect';

import { usePromoteOrShareActionUrls } from '../../../usePromoteOrShareActionUrls';

const COPY_REDIRECT_DELAY = 2000;

const generateRedirectUrl = (shareButtonUrl: string, channel: ShareChannel) => {
	const [baseUrl, queryString] = shareButtonUrl.split('?');

	const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};

	return `${baseUrl}${qs.stringify(
		{
			...queryParams,
			slide_up_source: channel,
		},
		{ addQueryPrefix: true },
	)}`;
};

type UseRedirectResult = {
	redirect: (channel: ShareChannel) => void;
	/**
	 * Supersedes the copy-link redirect when a later channel tap should keep the user here — wired to
	 * `onBeforeShare` in `../options.ts`, which documents why (CHANGE-97962).
	 *
	 * Clearing the armed timer is not enough on its own: `redirect('copy')` isn't reached until
	 * `./index.ts` finishes awaiting `createNewShare`, a real mutation that runs whenever the signer
	 * kept their signature public (the sign form's default). A tap landing during that round trip
	 * would find nothing armed yet, and the copy continuation would then arm a fresh timer. So taps
	 * are also counted, and a copy continuation only arms if its own tap is still the latest one.
	 *
	 * Must stay referentially stable: it ends up in `ShareActionsOptions`, and an identity change
	 * there rebuilds every channel's `ShareAction` component (see the `shareCopyOverride` note in
	 * `useShareActions/index.ts`).
	 */
	onShareTapped: (channel: ShareChannel) => void;
};

export function useRedirect(): UseRedirectResult {
	const {
		data: { shareButtonUrl },
	} = usePromoteOrShareActionUrls();

	const {
		userAgent: { isWebview },
	} = useUtilityContext();

	const { cancelableNavigate } = useCancelableNavigate();

	const timeoutRef = useRef<number | undefined>(undefined);
	// Both start at 0 so a `redirect('copy')` that never saw a tap (any consumer not wiring
	// `onBeforeShare`) still arms, rather than being silently superseded by a tap that never happened.
	const tapCountRef = useRef(0);
	const copyTapRef = useRef(0);

	const clearPendingRedirect = useCallback(() => {
		window.clearTimeout(timeoutRef.current);
		timeoutRef.current = undefined;
	}, []);

	const onShareTapped = useCallback(
		(channel: ShareChannel) => {
			clearPendingRedirect();
			tapCountRef.current += 1;
			// Copy records the tap it just created, so its own tap never supersedes its own redirect.
			if (channel === 'copy') {
				copyTapRef.current = tapCountRef.current;
			}
		},
		[clearPendingRedirect],
	);

	useEffect(() => clearPendingRedirect, [clearPendingRedirect]);

	return {
		redirect: useCallback(
			(channel: ShareChannel) => {
				if (!canRedirectAfterShare({ channel, isWebview })) return;

				const redirectUrl = generateRedirectUrl(shareButtonUrl, channel);

				if (channel === 'copy') {
					// A later tap happened while this copy share was still in flight — it owns the page now.
					if (copyTapRef.current !== tapCountRef.current) return;

					clearPendingRedirect();
					timeoutRef.current = window.setTimeout(() => {
						timeoutRef.current = undefined;
						cancelableNavigate({
							eventName: 'shareChannelSelectedRedirect',
							eventConfig: {
								selectedChannel: channel,
							},
							redirectUrl,
							deferWhileHidden: true,
						});
					}, COPY_REDIRECT_DELAY);
					return;
				}

				cancelableNavigate({
					eventName: 'shareChannelSelectedRedirect',
					eventConfig: {
						selectedChannel: channel,
					},
					redirectUrl,
					deferWhileHidden: true,
				});
			},
			[isWebview, cancelableNavigate, shareButtonUrl, clearPendingRedirect],
		),
		onShareTapped,
	};
}
